import { useState } from 'react';
import { CheckOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';

import { Bento, Tile, TileBody } from '../../../../components/shared';
import { Badge, Button, Checkbox, Empty, Select, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { useLuuMaTranKhuon, useMaTranKhuon } from '../../hooks/useQuyen';
import {
  coThayDoi,
  dao,
  dongKhuonRong,
  duocDungKhuon,
  tomTatKhuon,
  type VaiQuyen,
} from '../../nghiep-vu/quyen-du-lieu';
import type { KhuonGrant, KhuonLevel } from '../../types';
import { DaiLoi } from '../chung/VungDuLieu';
import { DanhSachVai } from './DanhSachVai';

/**
 * Phân quyền theo KHUÔN — ma trận thứ hai, độc lập với ma trận theo bảng/cột (port `QuyenKhuonTab` native).
 *
 * Một nguồn chạy MỘT chế độ khai báo: `cau_hoi` thì thẩm quyền là bảng và cột, `khuon` thì là nhóm và khuôn. Nhét cả hai
 * vào một bảng thì mỗi dòng mang hai nghĩa tuỳ chế độ. Cái giá của việc tách phải hiện ra: ma trận của chế độ đang NGỦ vẫn
 * sửa được nhưng chưa có hiệu lực — tab mở đầu bằng đúng câu ấy.
 *
 * `chan` THẮNG CẢ `full`: nếu chặn chỉ thắng ở một mức thì cùng một ô cho hai kết quả tuỳ mức. Vì thế cột "Chặn riêng"
 * bật ở MỌI mức khác `none`.
 */
export function QuyenKhuonTab({ sourceId, dsVai, suaDuoc }: { sourceId: string; dsVai: VaiQuyen[]; suaDuoc: boolean }) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const q = useMaTranKhuon(sourceId);
  const luu = useLuuMaTranKhuon(sourceId);
  const [vaiTho, setVai] = useState('');
  // Bản nháp gắn với nguồn: đổi nguồn là bỏ bản nháp của nguồn cũ.
  const [nhapTho, setNhap] = useState<{ sourceId: string; du: Record<string, KhuonGrant> } | null>(null);

  if (q.error) return <DaiLoi loi={q.error} onThuLai={() => void q.refetch()} />;
  if (!q.data) return <Skeleton className="h-64 w-full" />;

  const nhomDs = q.data.nhom;
  if (nhomDs.length === 0) {
    return <Empty title={t('troLyAiBotQuyenKhuon.chuaCoKhuon')} description={t('troLyAiBotQuyenKhuon.chuaCoKhuonMoTa')} />;
  }

  const goc = Object.fromEntries(q.data.grants.map((g) => [g.role, g]));
  const nhap = nhapTho && nhapTho.sourceId === sourceId ? nhapTho.du : goc;
  // Vai đang chọn phải là một vai CÓ THẬT trong danh sách — rơi về vai đầu thay vì giữ một khoá không khớp ô nào.
  const vai = dsVai.some((a) => a.role === vaiTho) ? vaiTho : (dsVai[0]?.role ?? '');
  const grant = nhap[vai] ?? dongKhuonRong(vai);
  const doi = coThayDoi(nhapTho && nhapTho.sourceId === sourceId ? nhapTho.du : null, q.data.grants);
  const khoa = !suaDuoc;

  const sua = (p: Partial<KhuonGrant>) =>
    setNhap({ sourceId, du: { ...nhap, [vai]: { ...(nhap[vai] ?? dongKhuonRong(vai)), ...p, role: vai } } });

  const tomTat = (role: string) => {
    const tt = tomTatKhuon(nhap[role]);
    switch (tt.ma) {
      case 'none':
        return t('troLyAiBotQuyenKhuon.tomTatNone');
      case 'full':
        return t('troLyAiBotQuyenKhuon.tomTatFull');
      case 'chan':
        return t('troLyAiBotQuyenKhuon.tomTatChan', { n: soVN(tt.n, locale) });
      default:
        return t('troLyAiBotQuyenKhuon.tomTatChon', { nhom: soVN(tt.nhom, locale), khuon: soVN(tt.khuon, locale) });
    }
  };

  const mucDs: { value: KhuonLevel; label: string }[] = [
    { value: 'none', label: t('troLyAiBotQuyenKhuon.mucNone') },
    { value: 'chon', label: t('troLyAiBotQuyenKhuon.mucChon') },
    { value: 'full', label: t('troLyAiBotQuyenKhuon.mucFull') },
  ];

  const luuNgay = () =>
    luu.mutate(
      Object.values(nhap).filter((g) => g.role in goc || g.level !== 'none'),
      {
        onSuccess: () => {
          toast.success(t('troLyAiBotQuyenKhuon.daLuu'));
          setNhap(null);
        },
        onError: (e) => toast.error(moTaLoi(e)),
      },
    );

  return (
    <div className="space-y-4">
      {/* Không nói ra thì người vận hành mở quyền xong và không hiểu vì sao trợ lý vẫn trả lời như trước. */}
      {q.data.cheDo === 'cau_hoi' && (
        <div className="flex items-start gap-1.5 rounded border border-warning-500 bg-warning-50 px-3 py-2 text-xs text-warning-700">
          <WarningOutlined className="mt-px shrink-0" />
          <span>{t('troLyAiBotQuyenKhuon.dangNgu')}</span>
        </div>
      )}

      <div className="flex items-center justify-end">
        {suaDuoc ? (
          <Button variant="primary" size="md" disabled={!doi} loading={luu.isPending} onClick={luuNgay}>
            {doi ? t('troLyAiBotQuyenKhuon.luu') : t('troLyAiBotQuyenDuLieu.daLuu')}
          </Button>
        ) : (
          <Badge variant="neutral">{t('troLyAiBotQuyenDuLieu.chiXem')}</Badge>
        )}
      </div>

      <Bento>
        <DanhSachVai ds={dsVai} chon={vai} onChon={setVai} tomTat={tomTat} />

        <Tile span="lg:col-span-9" className="min-w-0">
          <TileBody className="space-y-3">
            <Select<KhuonLevel>
              aria-label={t('troLyAiBotQuyenKhuon.muc')}
              className="w-full sm:w-80"
              value={grant.level}
              disabled={khoa || !vai}
              onChange={(v) => sua({ level: v })}
              options={mucDs}
            />

            {grant.level === 'none' ? (
              <p className="text-sm text-content-muted">{t('troLyAiBotQuyenKhuon.mucNone')}</p>
            ) : (
              <div className="space-y-3">
                {nhomDs.map((n) => {
                  const caNhom = grant.nhom.includes(n.id);
                  return (
                    <div key={n.id} className="rounded border border-default p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        {grant.level === 'chon' ? (
                          <Checkbox checked={caNhom} disabled={khoa} onChange={() => sua({ nhom: dao(grant.nhom, n.id) })}>
                            <span className="text-sm font-medium">{n.ten}</span>
                          </Checkbox>
                        ) : (
                          <span className="text-sm font-medium">{n.ten}</span>
                        )}
                        <Badge variant="neutral">{t('troLyAiBotQuyenKhuon.soKhuon', { n: soVN(n.khuon.length, locale) })}</Badge>
                      </div>

                      <ul className="mt-2 space-y-1">
                        {n.khuon.map((k) => {
                          const biChan = grant.chan.includes(k.id);
                          const capRieng = grant.khuon.includes(k.id);
                          const duocDung = duocDungKhuon(grant, n.id, k.id);
                          return (
                            <li
                              key={k.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-subtle"
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <span
                                  aria-hidden
                                  className={cn('size-1.5 shrink-0 rounded-full', duocDung ? 'bg-success-600' : 'bg-dark-300')}
                                />
                                <span className="sr-only">
                                  {duocDung ? t('troLyAiBotQuyenKhuon.duocDung') : t('troLyAiBotQuyenKhuon.khongDung')}
                                </span>
                                <span className="truncate text-sm">{k.ten}</span>
                                {!k.vaoBoChon && (
                                  <span title={t('troLyAiBotQuyenKhuon.chuaChayThuViSao')}>
                                    <Badge variant="warning">{t('troLyAiBotQuyenKhuon.chuaChayThuNhan')}</Badge>
                                  </span>
                                )}
                              </span>
                              <span className="flex shrink-0 flex-wrap items-center gap-3">
                                {grant.level === 'chon' && !caNhom && (
                                  <Checkbox
                                    checked={capRieng}
                                    disabled={khoa || biChan}
                                    onChange={() => sua({ khuon: dao(grant.khuon, k.id) })}
                                  >
                                    <span className="text-xs text-content-muted">
                                      <CheckOutlined /> {t('troLyAiBotQuyenKhuon.capRiengNhan')}
                                    </span>
                                  </Checkbox>
                                )}
                                <Checkbox checked={biChan} disabled={khoa} onChange={() => sua({ chan: dao(grant.chan, k.id) })}>
                                  <span className="text-xs text-content-muted">
                                    <StopOutlined /> {t('troLyAiBotQuyenKhuon.chanNhan')}
                                  </span>
                                </Checkbox>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
                <p className="text-xs text-content-muted">{t('troLyAiBotQuyenKhuon.chanViSao')}</p>
              </div>
            )}
          </TileBody>
        </Tile>
      </Bento>
    </div>
  );
}
